import { Form } from '@melio/penny';
import { useMelioIntl } from '@melio/platform-i18n';
import { useEffect } from 'react';
import { object, SchemaOf, string } from 'yup';

import { AddVendorFormFields } from '../../../../types';
import { useAddVendorForm } from '../../../../useAddVendorForm';
import { useAccountNumberSchema, useNicknameSchema, useZipCodeSchema } from '../../../AddVendorDetailsBaseSchema';
import { CompanyField } from '../../../CompanyField';
import { AddManagedVendorFormProps } from '../../types';
import { getCreateVendorPayload } from './utils';

export type AddFiservManagedVendorDetailsFormFields = Pick<
  AddVendorFormFields,
  'companyName' | 'nickname' | 'accountNumber' | 'confirmAccountNumber' | 'postalCode'
>;

const useSchema = (isZipCodeNeeded?: boolean) => {
  const { formatMessage } = useMelioIntl();

  const accountNumberSchema = useAccountNumberSchema();
  const nicknameSchema = useNicknameSchema();
  const zipCodeSchema = useZipCodeSchema(isZipCodeNeeded);

  return object()
    .shape({
      companyName: string().required(formatMessage('widgets.addVendorForm.companyName.validation.required')),
    })
    .concat(zipCodeSchema)
    .concat(nicknameSchema)
    .concat(accountNumberSchema) as SchemaOf<AddFiservManagedVendorDetailsFormFields>;
};

export const AddFiservManagedVendorForm = ({
  onClearCompanyNameSearchField,
  inlineApiErrorCodes,
  onSelectCompanyName,
  onSubmit,
  onSubmissionStateChange,
  shouldHideCompanyNameField,
  managed,
  defaultValues,
  isSaving,
  ...props
}: AddManagedVendorFormProps) => {
  const { formatMessage } = useMelioIntl();

  const { formProps, registerField, setValue, reset } = useAddVendorForm<AddFiservManagedVendorDetailsFormFields>({
    inlineApiErrorCodes,
    onSubmit,
    getPostVendorPayloadFromFormValues: (data) => getCreateVendorPayload(data, managed),
    schema: useSchema(managed.isZipCodeNeeded),
    defaultValues,
    isSaving,
    onSubmissionStateChange,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <Form
      data-component="AddVendorFormWidget"
      {...props}
      {...formProps}
      onSubmit={(e) => {
        e.stopPropagation();
        formProps.onSubmit(e);
      }}
      columns={2}
    >
      {!shouldHideCompanyNameField && (
        <CompanyField
          colSpan={2}
          onSelectCompanyName={onSelectCompanyName}
          onClearSearchField={() => {
            setValue('companyName', '');
            onClearCompanyNameSearchField?.();
          }}
          {...registerField('companyName')}
          isBusiness
        />
      )}
      <Form.TextField
        colSpan={2}
        {...registerField('nickname')}
        labelProps={{ label: formatMessage('widgets.addVendorForm.nickname.label') }}
        placeholder={formatMessage('widgets.addVendorForm.nickname.placeholder')}
        autoFocus={shouldHideCompanyNameField}
      />
      <Form.TextField
        {...registerField('accountNumber')}
        labelProps={{
          tooltipProps: {
            label: formatMessage('widgets.addVendorForm.accountNumber.labelTooltip'),
          },
          label: formatMessage('widgets.addVendorForm.accountNumber.label'),
        }}
      />
      <Form.TextField
        {...registerField('confirmAccountNumber')}
        labelProps={{
          label: formatMessage('widgets.addVendorForm.confirmAccountNumber.label'),
        }}
      />
      {managed.isZipCodeNeeded ? (
        <Form.TextField
          colSpan={2}
          labelProps={{
            label: formatMessage('widgets.addVendorForm.postalCode.label'),
          }}
          {...registerField('postalCode')}
        />
      ) : null}
    </Form>
  );
};

AddFiservManagedVendorForm.displayName = 'AddVendorFormWidget';
